import { kuunda } from '@/lib/kuunda';
import { displayNotes, leadDisplayName, type CrmLead } from './syncInquiries';

function splitName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0]!, last_name: '' };
  return { first_name: parts[0]!, last_name: parts.slice(1).join(' ') };
}

function customerNotesFromLead(lead: CrmLead): string {
  const parts: string[] = [];
  const notes = displayNotes(lead);
  if (notes) parts.push(notes);
  if (lead.opportunity?.trim()) parts.push(`Opportunité CRM : ${lead.opportunity.trim()}`);
  if (Number(lead.budget) > 0) {
    parts.push(`Valeur : ${Number(lead.budget).toLocaleString('fr-FR')} €`);
  }
  return parts.join('\n');
}

function leadToCustomerPayload(lead: CrmLead, userId: string) {
  const split = splitName(leadDisplayName(lead));
  return {
    user_id: userId,
    lead_id: lead.id,
    first_name: lead.first_name?.trim() || split.first_name,
    last_name: lead.last_name?.trim() || split.last_name,
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    company: lead.company ?? '',
    address: lead.location ?? '',
    notes: customerNotesFromLead(lead),
    purchase_count: 1,
    last_purchase_at: new Date().toISOString(),
  };
}

/** Importe les prospects gagnés absents de la base clients (idempotent). */
export async function syncWonLeadsToCustomers(userId: string): Promise<string | null> {
  const { data: wonLeads, error: leadsErr } = await kuunda
    .from('crm_leads')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'gagne');

  if (leadsErr) return leadsErr.message;

  const { data: customers, error: customersErr } = await kuunda
    .from('crm_customers')
    .select('id, lead_id')
    .eq('user_id', userId);

  if (customersErr) return customersErr.message;

  const linkedLeadIds = new Set(
    (customers ?? [])
      .map((c) => (c as { lead_id?: string | null }).lead_id)
      .filter((id): id is string => Boolean(id)),
  );

  for (const raw of wonLeads ?? []) {
    const leadId = String(raw.id);
    if (linkedLeadIds.has(leadId)) continue;

    const contact_name = String(raw.contact_name ?? '');
    const split = splitName(contact_name);
    const lead: CrmLead = {
      id: leadId,
      contact_name,
      first_name: String(raw.first_name || split.first_name),
      last_name: String(raw.last_name || split.last_name),
      email: String(raw.email ?? ''),
      phone: String(raw.phone ?? ''),
      location: String(raw.location ?? ''),
      company: String(raw.company ?? ''),
      opportunity: String(raw.opportunity ?? ''),
      budget: Number(raw.budget ?? 0),
      status: 'gagne',
      source: String(raw.source ?? ''),
      priority: String(raw.priority ?? 'normal'),
      notes: String(raw.notes ?? ''),
      project_id: (raw.project_id as string | null) ?? null,
      inquiry_id: (raw.inquiry_id as string | null | undefined) ?? null,
    };

    const payload = leadToCustomerPayload(lead, userId);
    const { error: insErr } = await kuunda.from('crm_customers').insert(payload);

    if (insErr) {
      const { lead_id: _leadId, ...fallback } = payload;
      const { error: fallbackErr } = await kuunda.from('crm_customers').insert(fallback);
      if (fallbackErr) return fallbackErr.message;
    }

    linkedLeadIds.add(leadId);
  }

  return null;
}

/** Synchronise un prospect gagné vers la base clients. */
export async function syncWonLeadToCustomer(lead: CrmLead, userId: string): Promise<string | null> {
  if (lead.status !== 'gagne') return null;

  const { data: existing, error: findErr } = await kuunda
    .from('crm_customers')
    .select('id')
    .eq('user_id', userId)
    .eq('lead_id', lead.id)
    .maybeSingle();

  if (findErr && findErr.code !== 'PGRST116') return findErr.message;
  if (existing?.id) return null;

  const payload = leadToCustomerPayload(lead, userId);
  const { error: insErr } = await kuunda.from('crm_customers').insert(payload);

  if (insErr) {
    const { lead_id: _leadId, ...fallback } = payload;
    const { error: fallbackErr } = await kuunda.from('crm_customers').insert(fallback);
    if (fallbackErr) return fallbackErr.message;
  }

  return null;
}
